import { isThreeDSecureChallenge, type ThreeDSecureChallenge } from 'pb-billing-ui/attempt/three-d-secure.ts';
import type { BaseAPIResponse, Meta } from '~/types/api';
import type {
    ApprovedCharge,
    BusinessBillingSummary,
    BusinessInvoice,
    BusinessProposal,
    InvoicePayOutcome,
    InvoicePayPreview,
    License,
} from '~/types/business';
import { pbRequest, pbRequestPage, pbRequestRaw, pbRequestVoid } from '~/services/request';
import { ApiError } from '~/utils/errors';

// License inventory, billing summary, invoices, payment methods, and
// seat auto-renewal.
export const billingService = {
    /**
     * Get available licenses
     */
    async getLicenses(companyId: number): Promise<License[]> {
        const envelope = await pbRequestRaw<BaseAPIResponse<any[]>>(`/companies/${companyId}/licenses`, {
            failure: 'Failed to fetch licenses',
        });

        // Map API response to License format
        // API returns: id (slug), name, price (cents), num_available, num_assigned, num_active
        // num_active = total purchased, num_available = unassigned, num_assigned = assigned
        return envelope.data.map((l: any) => ({
            slug: l.id, // API returns slug as 'id'
            name: l.name,
            price: l.price || 0, // Price in cents from DB
            available: l.num_available || 0,
            pool: l.num_assigned || 0, // seats a person holds (D10)
            purchased: typeof l.num_active === 'number' ? l.num_active : undefined,
        }));
    },

    /**
     * Get license information for premium licenses display
     * Uses the same logic as V1 getLicenseInformation endpoint
     */
    async getLicenseInformation(companyId: number): Promise<License[]> {
        const envelope = await pbRequestRaw<BaseAPIResponse<any[]>>(
            `/companies/${companyId}/analytics/license-information`,
            {
                failure: 'Failed to fetch license information',
            }
        );

        // Map API response - returns: slug, name, purchased, available, assigned, provisioned, given, pool
        return envelope.data.map((l: any) => ({
            slug: l.slug,
            name: l.name,
            available: l.available || 0,
            // D10: seats a person holds. `provisioned` has always meant that; `assigned`
            // counted every controlled seat until the API redefined it to the same thing.
            pool: l.provisioned ?? l.assigned ?? 0,
            purchased: typeof l.purchased === 'number' ? l.purchased : undefined,
        }));
    },

    /**
     * Get billing summary
     */
    async getBillingSummary(companyId: number): Promise<BusinessBillingSummary> {
        return await pbRequest<BusinessBillingSummary>(`/companies/${companyId}/financial/summary`, {
            failure: 'Failed to fetch billing summary',
        });
    },

    /**
     * Bulk-toggle auto_renew on every owned seat in a block. The backend
     * skips seats already on the target value, so calling with the same
     * value twice is a cheap no-op.
     */
    async setBlockAutoRenew(
        companyId: number,
        licenseId: number,
        nextBillDate: string | null,
        autoRenew: boolean,
    ): Promise<{ updated: number; auto_renew: boolean }> {
        const body: Record<string, unknown> = { license_id: licenseId, auto_renew: autoRenew };
        if (nextBillDate) body.next_bill_date = nextBillDate;
        return await pbRequest<{ updated: number; auto_renew: boolean }>(
            `/companies/${companyId}/financial/license-blocks/auto-renew`,
            {
                method: 'POST', body,
                failure: 'Failed to update block auto-renew',
            }
        );
    },

    /**
     * Toggle auto_renew on a single seat owned by the company. Reuses the
     * pending-renewal-invoice rebuild on the backend so any open renewal
     * invoice tracks the new intent.
     */
    async setLicenseAutoRenew(
        companyId: number,
        personLicenseId: number,
        autoRenew: boolean,
    ): Promise<{ id: number; auto_renew: boolean }> {
        return await pbRequest<{ id: number; auto_renew: boolean }>(
            `/companies/${companyId}/financial/licenses/${personLicenseId}/auto-renew`,
            {
                method: 'POST',
                body: { auto_renew: autoRenew },
                failure: 'Failed to update auto-renew',
            }
        );
    },

    /** A page of the invoices the company pays: its own, and those billed to its channel partner account. */
    async getInvoices(
        companyId: number,
        params?: {
            page?: number;
            per_page?: number;
            search?: string;
            status?: string;
            type?: string;
            start_date?: string;
            end_date?: string;
            sort_field?: string;
            sort_order?: string;
        }
    ): Promise<{ data: BusinessInvoice[]; meta: Meta }> {
        const query = new URLSearchParams();
        if (params?.page) query.append('page', params.page.toString());
        if (params?.per_page) query.append('per_page', params.per_page.toString());
        if (params?.search) query.append('search', params.search);
        if (params?.status) query.append('status', params.status);
        if (params?.type) query.append('type', params.type);
        if (params?.start_date) query.append('start_date', params.start_date);
        if (params?.end_date) query.append('end_date', params.end_date);
        if (params?.sort_field) query.append('sort_field', params.sort_field);
        if (params?.sort_order) query.append('sort_order', params.sort_order);

        return await pbRequestPage<BusinessInvoice>(`/companies/${companyId}/financial/invoices?${query.toString()}`, {
            failure: 'Failed to fetch invoices',
        });
    },

    /**
     * Download invoice PDF
     */
    async downloadInvoice(companyId: number, invoiceId: string): Promise<Blob> {
        return await pbRequestRaw<Blob>(`/companies/${companyId}/financial/invoices/${invoiceId}`, {
            responseType: 'blob',
            failure: 'Failed to download invoice',
        });
    },

    /**
     * Update payment method
     */
    async updatePaymentMethod(
        companyId: number,
        paymentData: {
            payment_method: string;
            first_name: string;
            last_name: string;
        }
    ): Promise<{ success: boolean; card_brand: string; card_last_four: string }> {
        const envelope = await pbRequestRaw<BaseAPIResponse<{ success: boolean; card_brand: string; card_last_four: string }>>(
            `/companies/${companyId}/financial/payment-method`,
            {
                method: 'POST',
                body: paymentData,
                failure: 'Failed to update payment method',
            }
        );

        return envelope.data;
    },

    /**
     * Remove payment method
     */
    async removePaymentMethod(companyId: number): Promise<void> {
        await pbRequestVoid(`/companies/${companyId}/financial/payment-method`, {
            method: 'DELETE',
            expect: [200, 204],
            failure: 'Failed to remove payment method',
        });
    },

    /**
     * What paying one invoice would take, without taking it (PB-065-API).
     *
     * A server read of the charge's own code path, not a figure this client
     * derives: account credit means the amount an invoice is FOR and the amount
     * the card would be charged are two different numbers, and a credit-covered
     * invoice quoted from the row would promise its full total and then charge
     * nothing.
     *
     * Refused with a 422 for an invoice there is no figure to promise: a renewal,
     * whose total the charge recalculates as it runs, and anything the charge
     * itself would refuse — one no longer payable, one with a payment already in
     * flight, one of a type this route does not settle. A refusal carries no
     * amount, so it is not a number to render: the caller shows the invoice's own
     * total instead and pins nothing. An invoice that is gone or is another
     * company's is the route binding's empty 404, one answer for both.
     */
    async getInvoicePayPreview(companyId: number, invoiceId: string): Promise<InvoicePayPreview> {
        return await pbRequest<InvoicePayPreview>(
            `/companies/${companyId}/financial/invoices/${invoiceId}/pay-preview`,
            { failure: 'Failed to read what paying this invoice would take' },
        );
    },

    /**
     * Pay one invoice. One charge, one invoice — which is what the refund,
     * dispute and reconciliation paths assume.
     *
     * `approved` is what the payer was shown, echoed back under the preview's own
     * field names. Send it and the charge is held to it: if the account credit or
     * the primary card moved between the preview and the button, the API refuses
     * with a 422 carrying `reason: "quote_stale"` and the card is untouched.
     * Omit it and nothing is promised, so nothing is pinned — which is the only
     * honest shape for an invoice that has no preview.
     *
     * A 200 is not always the invoice paid. A card whose bank must authenticate the
     * charge is answered with app-api's bank step (`ThreeDSecureV3Resource`) in place
     * of the paid invoice (`InvoicePaymentV3Resource`), and nothing is taken until
     * the payer completes it: that answer comes back as `requires_action`. Paid is
     * only the answer that says so (`success: true`); any other 200 is one this
     * client cannot read, and throws, so it is never taken for a payment.
     */
    async payInvoice(companyId: number, invoiceId: string, approved?: ApprovedCharge): Promise<InvoicePayOutcome> {
        const failure = 'Failed to process payment';
        const answer = await pbRequest<ThreeDSecureChallenge | { success?: boolean }>(
            `/companies/${companyId}/financial/invoices/${invoiceId}/pay`,
            { method: 'POST', body: approved, failure },
        );

        if (isThreeDSecureChallenge(answer)) return { kind: 'requires_action', action: answer };
        if (answer.success === true) return { kind: 'paid' };

        throw new ApiError(failure, 200);
    },

    /**
     * The company's proposals, newest first (PB-082).
     *
     * Unpaginated by design — a company has a handful, because staff build them one
     * at a time for a negotiated deal — so the Proposals tab slices its own history
     * the way the licence blocks beside it do.
     */
    async getProposals(companyId: number): Promise<BusinessProposal[]> {
        const envelope = await pbRequestRaw<BaseAPIResponse<BusinessProposal[]>>(
            `/companies/${companyId}/financial/proposals`,
            { failure: 'Failed to load proposals' },
        );

        return envelope.data ?? [];
    },
};
